import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useApi } from '../lib/hooks'
import { money, num, titleCase } from '../lib/format'
import {
  Banner, ChainNode, Empty, ErrorNote, Loading, Panel, Pill, StatMini, TraceChain,
} from '../ui/kit'
import { FieldHeader } from './FieldShell'

interface Trace {
  id: string; code: string; status: string
  farmer: { name: string; code: string; zone: string }
  crop: { name: string }
  grade: { code: string; spec: string }
  fifoBatch: string | null
  pricePerKg: number
  farmgateKg: number | null; depotInKg: number | null; deliveredKg: number | null
  purchaseValue: number | null
  totalVariancePct: number | null; unexplainedPct: number | null
  chain: Array<{
    point: string; netKg: number; weighedAt: string
    weighedBy: { name: string } | null; scale: { code: string | null } | null
    hasPhoto: boolean; hasGps: boolean; temperatureC: number | null
  }>
  variances: Array<{
    id: string; fromPoint: string; toPoint: string; deltaKg: number; deltaPct: number
    withinTolerance: boolean; reasonCode: string; valueAtRisk: number | null
    splits: Array<{ reasonCode: string; label: string | null; deltaPct: number }>
  }>
  payment: { status: string; amount: number; accountRef: string; settledAt: string | null } | null
  rejections: Array<{
    code: string; rejectedKg: number; buyer: string; disposition: string
    returnWeightKg: number | null; capturedLoss: number | null
  }>
}

/** Lot lookup by code — the screen a person opens when a buyer queries a delivery. */
export function FieldLot() {
  const { code } = useParams()
  const nav = useNavigate()
  const [query, setQuery] = useState('')
  const searching = code === 'search'

  const trace = useApi<Trace>(searching ? null : `/collections/lots/${code}`)

  if (searching) {
    return (
      <>
        <FieldHeader />
        <div className="field-greet">
          <h1>Find a collection</h1>
          <p>Type the code on the crate label to see its whole chain.</p>
        </div>
        <div className="field-body">
          <form
            onSubmit={(e) => {
              e.preventDefault()
              if (query.trim()) nav(`/m/lot/${query.trim().toUpperCase()}`)
            }}
          >
            <div className="field">
              <label htmlFor="lotcode">Collection number</label>
              <input
                id="lotcode"
                className="input mono"
                style={{ fontSize: 'var(--fs-xl)', minHeight: 56, textAlign: 'center', letterSpacing: '.06em' }}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="FBN-0911-001"
                autoCapitalize="characters"
              />
            </div>
            <button className="btn lg block" style={{ marginTop: 12 }} disabled={!query.trim()}>
              Open trace
            </button>
          </form>
          <Banner tone="info" title="Every step is recorded">
            The weight, the time, where it happened, a photo and who did it — at the farm, at the
            store and at the buyer's door. That record is what lets you argue back when a buyer
            sends something back.
          </Banner>
        </div>
      </>
    )
  }

  if (trace.loading) {
    return (
      <>
        <FieldHeader back={() => nav('/m/lot/search')} title="Follow a collection" />
        <div className="field-body"><Panel><Loading rows={5} /></Panel></div>
      </>
    )
  }

  if (trace.error || !trace.data) {
    return (
      <>
        <FieldHeader back={() => nav('/m/lot/search')} title="Follow a collection" />
        <div className="field-body">
          {trace.error
            ? <ErrorNote error={trace.error} onRetry={trace.reload} />
            : <Empty title="Nothing with that number" />}
        </div>
      </>
    )
  }

  const t = trace.data
  const open = t.variances.find((v) => !v.withinTolerance && v.reasonCode === 'UNEXPLAINED')

  const nodes: ChainNode[] = t.chain.map((w, i) => {
    const v = t.variances.find((x) => x.toPoint === w.point)
    return {
      point: w.point,
      netKg: w.netKg,
      weighedBy: w.weighedBy,
      weighedAt: w.weighedAt,
      scale: w.scale,
      hasPhoto: w.hasPhoto,
      hasGps: w.hasGps,
      temperatureC: w.temperatureC,
      deltaPct: v?.deltaPct ?? null,
      tone: i === 0 ? 'ok' : v && !v.withinTolerance ? 'crit' : 'ok',
      attributions: v?.splits.map((sp) => ({ label: sp.label ?? sp.reasonCode, deltaPct: sp.deltaPct })),
    }
  })

  return (
    <>
      <FieldHeader
        back={() => nav('/m/lot/search')}
        title="Follow a collection"
        right={
          open
            ? <Pill tone="crit">Variance {t.unexplainedPct}%</Pill>
            : <Pill tone="ok">{titleCase(t.status)}</Pill>
        }
      />

      <div className="field-body">
        <Panel title={<h3 className="mono">{t.code}</h3>} meta={`${t.crop.name} · Grade ${t.grade.code}`}>
          <div className="panel-body">
            <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--ink-2)' }}>
              {t.farmer.name} · {t.farmer.zone}
              {t.fifoBatch && <> · FIFO batch {t.fifoBatch}</>}
            </p>
          </div>
        </Panel>

        {open && (
          <Banner tone="crit" title={`Chain variance ${open.deltaPct}% — outside tolerance`}>
            {Math.abs(Number(open.deltaKg)).toFixed(1)} kg unaccounted for
            {open.valueAtRisk ? <>, worth {money(open.valueAtRisk)}</> : null}. A reason must be
            assigned before this lot can close.
          </Banner>
        )}

        <Panel title="Chain of custody">
          <div className="panel-body">
            <TraceChain nodes={nodes} />
          </div>
        </Panel>

        <div className="mini-row">
          <StatMini label="At the farm" value={`${num(t.farmgateKg, 1)} kg`} />
          <StatMini label="At the store" value={t.depotInKg === null ? '—' : `${num(t.depotInKg, 1)} kg`} />
          <StatMini label="Paid" value={money(t.purchaseValue, { compact: true })} />
        </div>

        {t.payment && (
          <div className="payout">
            <span className="payout-mark">M</span>
            <div className="payout-who">
              <b>{titleCase(t.payment.status)}</b>
              <i>{t.payment.accountRef}</i>
            </div>
            <div className="payout-amt">
              <b>{money(t.payment.amount, { currency: false })}</b>
              <span>{t.payment.settledAt ? 'settled' : 'in flight'}</span>
            </div>
          </div>
        )}

        {t.rejections.length > 0 && (
          <Panel title="Rejections">
            <div className="panel-body col" style={{ gap: 10 }}>
              {t.rejections.map((r) => (
                <div key={r.code} className="spread" style={{ fontSize: 'var(--fs-sm)' }}>
                  <span>
                    <b className="mono">{r.code}</b>
                    <div className="faint" style={{ fontSize: 'var(--fs-xs)' }}>
                      {r.buyer} · {titleCase(r.disposition)}
                    </div>
                  </span>
                  <span className="num" style={{ textAlign: 'right' }}>
                    <b>{num(r.rejectedKg, 1)} kg</b>
                    <div className="faint" style={{ fontSize: 'var(--fs-xs)' }}>
                      {r.returnWeightKg === null ? 'not returned' : `${num(r.returnWeightKg, 1)} kg back`}
                    </div>
                  </span>
                </div>
              ))}
            </div>
          </Panel>
        )}

        <Panel title="Grade specification" meta={`Grade ${t.grade.code}`}>
          <div className="panel-body">
            <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--ink-2)', lineHeight: 1.6 }}>{t.grade.spec}</p>
          </div>
        </Panel>
      </div>
    </>
  )
}
